import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { MdCloudUpload } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import css from "./SemiFinishedLeftBlockModal.module.css";
import SemiFinishedProductListModal from "../SemiFinishedProductListModal/SemiFinishedProductListModal";
import SemiFinishedSemiFinishedListModal from "../SemiFinishedSemiFinishedListModal/SemiFinishedSemiFinishedListModal";

const SemiFinishedLeftBlockModal = ({
  setSemiFinishedProductAddOpen,
  setSemiFinishedSemiFinishedAddOpen,
  setUploadedImage,
  initialImage,
}) => {
  const { t } = useTranslation();
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const { semiFinishedProductsWeight, semiFinishedProductsWeightNetto } =
    useSelector((store) => store.semiFinishedProducts);

  const {
    semiFinishedSemiFinishedWeight,
    semiFinishedSemiFinishedWeightNetto,
  } = useSelector((store) => store.semiFinishedSemiFinished);

  const totalWeight =
    semiFinishedProductsWeight + semiFinishedSemiFinishedWeight;
  const totalWeightNetto =
    semiFinishedProductsWeightNetto + semiFinishedSemiFinishedWeightNetto;

  const [manualWeight, setManualWeight] = useState(totalWeight.toString());
  const [nettoWeight, setNettoWeight] = useState("");
  const [nettoRatio, setNettoRatio] = useState(0);

  const {
    register,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();

  // 1. Load initial image
  useEffect(() => {
    if (initialImage) {
      setPreviewImageUrl(initialImage);
    }
  }, [initialImage]);

  // 2. Initialize weightNet from form
  useEffect(() => {
    const initWeightNet = getValues("weightNet");
    if (initWeightNet !== undefined && initWeightNet !== null) {
      setNettoWeight(initWeightNet.toString());
    }
  }, []);

  // 3. Auto-set weight in form
  useEffect(() => {
    setManualWeight(totalWeight.toString());
    setValue("weight", totalWeight);
  }, [totalWeight, setValue]);

  // 4. Auto-set weightNetto in form
  useEffect(() => {
    setNettoWeight(totalWeightNetto.toString());
    setValue("weightNet", totalWeightNetto);
  }, [totalWeightNetto, setValue]);

  // 5. Calculate netto ratio automatically
  useEffect(() => {
    const weight = parseFloat(manualWeight) || 0;
    const weightNet = parseFloat(nettoWeight) || 0;
    const ratio = weight > 0 ? Number((weightNet / weight).toFixed(3)) : 0;
    setNettoRatio(ratio);
    setValue("nettoRatio", ratio);
  }, [manualWeight, nettoWeight, setValue]);

  // Photo upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadedImage(file);
    setPreviewImageUrl(URL.createObjectURL(file));
    event.target.value = null;
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setPreviewImageUrl(null);
  };

  // Weight change handler
  const handleWeightChange = (e) => {
    const value = e.target.value;
    setManualWeight(value);
    setValue("weight", Number(value));
  };

  // Netto weight change handler
  const handleNettoChange = (e) => {
    const value = e.target.value;
    setNettoWeight(value);
    setValue("weightNet", Number(value));
  };

  return (
    <div className={css.wrap}>
      <div className={css.uploadArea}>
        {previewImageUrl ? (
          <div className={css.previewContainer}>
            <img
              src={previewImageUrl}
              alt="preview"
              className={css.previewImage}
            />
            <button
              type="button"
              className={css.removeImageButton}
              onClick={handleRemoveImage}
            >
              ×
            </button>
          </div>
        ) : (
          <label>
            <input
              type="file"
              accept=".jpg, .jpeg, .png"
              onChange={handleFileUpload}
            />
            <div>
              <MdCloudUpload className={css.iconUpload} />
            </div>
            <p className={css.textUpload}>
              {t("Drop-file-here-or")}{" "}
              <span className={css.textUploadLink}>{t("click-to-upload")}</span>
            </p>
          </label>
        )}
      </div>

      <div className={css.inputBlock}>
        <div className={css.inputGroup}>
          <label htmlFor="weight">{t("Weight")}</label>
          <input
            type="number"
            id="weight"
            value={manualWeight}
            onChange={handleWeightChange}
            {...register("weight")}
            className={errors.weight ? css.inputError : ""}
          />
        </div>

        <div className={css.inputGroup}>
          <label htmlFor="weightNet">{t("weight-netto")}</label>
          <input
            type="number"
            id="weightNet"
            value={nettoWeight}
            onChange={handleNettoChange}
            className={errors.weightNet ? css.inputError : ""}
          />
        </div>

        <div className={css.inputGroup}>
          <label htmlFor="price">{t("modal-price")}</label>
          <input
            type="number"
            id="price"
            {...register("price")}
            className={errors.price ? css.inputError : ""}
          />
        </div>

        {/* New Netto Ratio field */}
        <div className={css.inputGroup}>
          <label htmlFor="nettoRatio">{t("Netto Ratio")}</label>
          <input
            type="number"
            id="nettoRatio"
            value={nettoRatio}
            readOnly
            className={css.readOnlyInput}
            step="0.001"
          />
        </div>
      </div>

      <div className={css.productsContainer}>
        <div className={css.header}>
          <div className={css.product}>{t("settings-menu-title-products")}</div>
          <div className={css.weight}>{t("Weight")} (g)</div>
          <div className={css.weightNetto}>{t("weight-netto")} (g)</div>
          <div className={css.price}>{t("Cost")}</div>
          <div className={css.add}>
            <button
              type="button"
              className={css.addButton}
              onClick={() => setSemiFinishedProductAddOpen(true)}
            >
              +
            </button>
          </div>
        </div>
        <div className={css.listProducts}>
          <SemiFinishedProductListModal />
        </div>
      </div>

      <div className={css.productsContainer}>
        <div className={css.header}>
          <div className={css.product}>
            {t("aside-menu-item-semi-finished")}
          </div>
          <div className={css.weight}>{t("Weight")} (g)</div>
          <div className={css.weightNetto}>{t("weight-netto")} (g)</div>
          <div className={css.price}>{t("Cost")}</div>
          <div className={css.add}>
            <button
              type="button"
              className={css.addButton}
              onClick={() => setSemiFinishedSemiFinishedAddOpen(true)}
            >
              +
            </button>
          </div>
        </div>
        <div className={css.listProducts}>
          <SemiFinishedSemiFinishedListModal />
        </div>
      </div>
    </div>
  );
};

export { SemiFinishedLeftBlockModal };
